import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';

import {
  createAttribute,
  createVariant,
  selectActiveAttributes,
} from '@polyai/stream/store/reducers/variant';
import { selectVariants } from '@polyai/stream/store/reducers/variant/selectors';

import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import { generateAttributeId, generateVariantId } from 'lib/prettyUuid';
import type VariantModal from 'screens/VariantManagement/components/VariantModal';
import NewVariantModal from 'screens/VariantManagement/components/VariantModal';

interface Attribute {
  id: string;
  name: string;
}

interface Props {
  onCreate?: (attribute: Attribute) => void;
  onCancel?: (error?: string) => void;
}

const VariantAttribute = ({ onCancel, onCreate }: Props) => {
  const serverDispatch = useServerDispatch();
  const attributes = useAppSelector(selectActiveAttributes);
  const variants = useAppSelector(selectVariants);

  const onAttributeAdd = useCallback(
    (attribute: Attribute) => {
      onCreate?.(attribute);
    },
    [onCreate],
  );

  const handleCreateAttribute = useMemo(() => {
    const create = (key: string, newValues: Record<string, string>) => {
      const isDefault = newValues.variantName;
      if (isDefault && Object.keys(newValues).length !== 2) {
        throw new Error(
          'There should not be a default variant name and other variants at the same time',
        );
      }
      const attributeId = generateAttributeId();
      if (isDefault) {
        const variantId = generateVariantId();
        onAttributeAdd({
          id: attributeId,
          name: key,
        });
        return serverDispatch(
          createVariant({
            id: variantId,
            name: newValues.variantName,
            attributeValues: {
              values: {},
            },
          }),
          createAttribute({
            id: attributeId,
            name: key,
            references: {
              topics: {},
              flowSteps: {},
              noCodeSteps: {},
            },
            variantValues: {
              values: { [variantId]: newValues.attributeValue },
            },
          }),
        );
      }
      onAttributeAdd({
        id: attributeId,
        name: key,
      });
      return serverDispatch(
        createAttribute({
          id: attributeId,
          name: key,
          references: {
            topics: {},
            flowSteps: {},
            noCodeSteps: {},
          },
          variantValues: { values: newValues },
        }),
      );
    };

    return (createAttribute: string, newValues: Record<string, string>) =>
      create(createAttribute, newValues).catch(error => {
        console.error('Error creating attribute:', error);
        onCancel?.('Error creating attribute');
      });
  }, [onAttributeAdd, serverDispatch, onCancel]);

  return (
    <NewVariantModal
      existingNames={attributes.map(a => a.name)}
      fieldConfig={
        !variants?.length
          ? {
              variantName: {
                label: 'Variant name',
                value: '',
              },
              attributeValue: {
                label: 'Attribute value',
                value: '',
              },
            }
          : variants.reduce(
              (vs, variant) => ({
                ...vs,
                [variant.id]: {
                  label: variant.name,
                  value: '',
                },
              }),
              {} as ComponentProps<typeof VariantModal>['fieldConfig'],
            )
      }
      type="attribute"
      open
      onSubmit={handleCreateAttribute}
    />
  );
};

export default VariantAttribute;
